import { useState, type FormEvent } from 'react';
import { SUCCESS_EVENT_KINDS, SUCCESS_KIND_LABELS, type Experiment } from '../../netlify/functions/_lib/domain.ts';
import Button from './Button.tsx';
import { Field, Select, TextArea, TextInput } from './Field.tsx';

export type ExperimentFields = {
  title: string;
  audience: string;
  message: string;
  offer: string;
  channel: string;
  success_event_kind: Experiment['success_event_kind'];
  success_label: string;
  success_threshold: number;
  status: Experiment['status'];
};

export function fieldsFrom(experiment?: Experiment): ExperimentFields {
  return {
    title: experiment?.title ?? '',
    audience: experiment?.audience ?? '',
    message: experiment?.message ?? '',
    offer: experiment?.offer ?? '',
    channel: experiment?.channel ?? '',
    success_event_kind: experiment?.success_event_kind ?? 'waitlist',
    success_label: experiment?.success_label ?? '',
    success_threshold: experiment?.success_threshold ?? 12,
    status: experiment?.status ?? 'draft',
  };
}

export default function ExperimentForm({
  initial,
  submitLabel,
  onSubmit,
}: {
  initial?: Experiment;
  submitLabel: string;
  onSubmit: (fields: ExperimentFields) => Promise<void>;
}) {
  const [fields, setFields] = useState(fieldsFrom(initial));
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  function update<K extends keyof ExperimentFields>(key: K, value: ExperimentFields[K]) {
    setFields((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setPending(true);
    try {
      await onSubmit(fields);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="space-y-5" onSubmit={(event) => void handleSubmit(event)}>
      <Field label="Title" hint="A name you will recognise in a month.">
        <TextInput
          required
          maxLength={120}
          value={fields.title}
          onChange={(e) => update('title', e.target.value)}
        />
      </Field>
      <Field label="Audience" hint="Who you will put this in front of.">
        <TextArea
          required
          maxLength={500}
          value={fields.audience}
          onChange={(e) => update('audience', e.target.value)}
        />
      </Field>
      <Field label="Message" hint="The sentence they will actually see.">
        <TextArea
          required
          maxLength={1000}
          value={fields.message}
          onChange={(e) => update('message', e.target.value)}
        />
      </Field>
      <Field label="Offer" hint="The step you are asking them to take.">
        <TextArea
          required
          maxLength={1000}
          value={fields.offer}
          onChange={(e) => update('offer', e.target.value)}
        />
      </Field>
      <Field label="Channel" hint="Where you will place the link. Not an integration.">
        <TextInput
          maxLength={200}
          value={fields.channel}
          onChange={(e) => update('channel', e.target.value)}
        />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="What counts">
          <Select
            value={fields.success_event_kind}
            onChange={(e) =>
              update('success_event_kind', e.target.value as ExperimentFields['success_event_kind'])
            }
          >
            {SUCCESS_EVENT_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {SUCCESS_KIND_LABELS[kind]}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Success count"
          hint="A whole number. Rates are shown later as context, never as a pass/fail gate."
        >
          <TextInput
            type="number"
            min={1}
            max={10000}
            required
            value={fields.success_threshold}
            onChange={(e) => update('success_threshold', Number(e.target.value))}
          />
        </Field>
      </div>
      {fields.success_event_kind === 'custom' ? (
        <Field label="Name of the custom count">
          <TextInput
            required
            maxLength={80}
            value={fields.success_label}
            onChange={(e) => update('success_label', e.target.value)}
          />
        </Field>
      ) : null}
      <Field label="Status">
        <Select
          value={fields.status}
          onChange={(e) => update('status', e.target.value as ExperimentFields['status'])}
        >
          <option value="draft">Draft</option>
          <option value="running">Running</option>
          <option value="decided">Decided</option>
          <option value="archived">Archived</option>
        </Select>
      </Field>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <Button type="submit" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  );
}
